import { Button, Label, Text } from "north.js";
import React from "react";
import styled from "styled-components";

import TooltipWrapper from "src/shared/components/Tooltip/Wrapper";
import { ErrorExclamationIcon } from "src/static/icons";

import { Shipment } from "../types";
import {
  formatShipmentDate,
  getArrivalDateColor,
  getShipmentLateWarning,
  getShipmentStageColor,
  getShipmentStageLabel,
} from "../utils";

export const ShipmentsTable = ({
  shipments,
  onEditClick,
}: {
  shipments: Shipment[];
  onEditClick: (index: number) => void;
}) => {
  if (shipments.length === 0) {
    return (
      <EmptyState>
        <Text type="body1">
          No shipments yet. Create your first shipment above.
        </Text>
      </EmptyState>
    );
  }

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <Th>Box description</Th>
            <Th>Total boxes</Th>
            <Th>Boxes arrived</Th>
            <Th>Estimated arrival</Th>
            <Th>Date received</Th>
            <Th>Status</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {shipments.map((shipment, index) => {
            const lateReason = getShipmentLateWarning(shipment);

            const dateColor = getArrivalDateColor(
              shipment.arrival_date,
              shipment.eta,
              shipment.include_in_swag_bags
            );

            return (
              <Tr
                key={`${shipment.description}-${index}`}
                $isLate={!!lateReason}
              >
                <Td>
                  <DescriptionCell>
                    <span>{shipment.description || "—"}</span>
                    {shipment.organizer_shipping_notes.trim() && (
                      <TooltipWrapper
                        id={`organizer-note-tooltip-${index}`}
                        icon={<OrganizerNoteDot />}
                        content={
                          <OrganizerNoteTooltipText>
                            Organizer note: {shipment.organizer_shipping_notes}
                          </OrganizerNoteTooltipText>
                        }
                        color="textDark"
                        direction="top"
                        padding={14}
                      />
                    )}
                  </DescriptionCell>
                </Td>
                <Td align="center">{shipment.total_boxes}</Td>
                <Td align="center">{shipment.boxes_received}</Td>
                <Td align="center">
                  {shipment.eta ? (
                    <EtaCellContent>
                      {!shipment.arrival_date ? (
                        <>
                          <Label color={dateColor} size="sm">
                            {formatShipmentDate(shipment.eta)}
                          </Label>
                          {lateReason && (
                            <LateIcon>
                              <TooltipWrapper
                                id={`late-tooltip-${index}`}
                                icon={
                                  <ErrorExclamationIcon
                                    width={16}
                                    height={16}
                                  />
                                }
                                content={lateReason}
                                color="textDark"
                                direction="top"
                                padding={12}
                              />
                            </LateIcon>
                          )}
                        </>
                      ) : (
                        <span>{formatShipmentDate(shipment.eta)}</span>
                      )}
                    </EtaCellContent>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td align="center">
                  {shipment.arrival_date ? (
                    <EtaCellContent>
                      <Label color={dateColor} size="sm">
                        {formatShipmentDate(shipment.arrival_date)}
                      </Label>
                      {lateReason && (
                        <LateIcon>
                          <TooltipWrapper
                            id={`late-arrival-tooltip-${index}`}
                            icon={
                              <ErrorExclamationIcon width={16} height={16} />
                            }
                            content={lateReason}
                            color="textDark"
                            direction="top"
                            padding={12}
                          />
                        </LateIcon>
                      )}
                    </EtaCellContent>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td align="center">
                  <Label
                    color={getShipmentStageColor(shipment.stage)}
                    size="sm"
                  >
                    {getShipmentStageLabel(shipment.stage)}
                  </Label>
                </Td>
                <Td align="center">
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => onEditClick(index)}
                  >
                    Edit
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  border-bottom: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};

  &:not(:last-child):not(:nth-last-child(2)) {
    border-right: 1px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }
`;

const Tr = styled.tr<{ $isLate?: boolean }>`
  background: ${({ $isLate }) =>
    $isLate ? "rgba(220, 38, 38, 0.07)" : "transparent"};

  &:not(:last-child) td {
    border-bottom: 1px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  ${({ $isLate }) =>
    $isLate &&
    `
    td {
      color: #b91c1c;
    }
  `}
`;

const Td = styled.td<{ align?: "left" | "center" | "right" }>`
  padding: 10px 16px;
  font-size: 14px;
  text-align: ${({ align }) => align ?? "left"};
  color: ${({ theme }) => theme.globalConstants.color.textBody};

  &:not(:last-child) {
    border-right: 1px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }
`;

const DescriptionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OrganizerNoteDot = styled.span`
  display: inline-flex;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
`;

const OrganizerNoteTooltipText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: normal;
  word-break: break-word;
`;

const EtaCellContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LateIcon = styled.div`
  position: absolute;
  right: -10px;
  display: flex;
  align-items: center;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
`;
